/**
 * Account page initialization
 */

import { initPasskeyManagement } from "./passkey-manage";
import { showToast } from "./web-components";

interface UserInfo {
  email: string;
  role: string;
  has_password: boolean;
  tenant: {
    name: string;
    app_url: string;
    manage_subscription_url?: string;
  };
  subscription?: {
    status: string;
    tier?: string;
    subscription_ends_at?: number;
  };
}

interface OAuthConnection {
  id: string;
  provider: string;
  provider_email?: string;
  connected_at: number;
}

interface OAuthProvider {
  name: string;
  displayName: string;
  authorizeUrl: string;
}

const PROVIDER_ICONS: Record<string, string> = {
  google:
    '<path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>',
  github:
    '<path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.17 6.839 9.49.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.463-1.11-1.463-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.167 22 16.418 22 12c0-5.523-4.477-10-10-10z"/>',
  microsoft:
    '<path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zm12.6 0H12.6V0H24v11.4z"/>',
  facebook:
    '<path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>',
  apple:
    '<path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>',
};

const STATUS_LABELS: Record<string, string> = {
  trial: "Trial",
  active: "Active",
  cancelled: "Cancelled",
  expired: "Expired",
  past_due: "Past Due",
  paused: "Paused",
};

let cachedManageUrl: string | null = null;

function formatDate(timestamp: number | null | undefined): string | null {
  if (!timestamp) return null;
  return new Date(timestamp * 1000).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatStatus(status: string): string {
  return STATUS_LABELS[status] || status;
}

function getEndDateLabel(status: string): string {
  const labels: Record<string, string> = {
    trial: "Trial ends",
    active: "Renews",
    past_due: "Renews",
    cancelled: "Ends",
    expired: "Expired",
    paused: "Paused until",
  };
  return labels[status] || "Ends";
}

function getEmailInitial(email: string): string {
  return email.charAt(0).toUpperCase();
}

function getNameFromEmail(email: string): string {
  const localPart = email.split("@")[0];
  return localPart
    .split(/[._-]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

async function loadUserInfo(): Promise<void> {
  try {
    const response = await fetch("/api/auth/me");
    if (!response.ok) {
      window.location.href = "/login";
      return;
    }

    const user: UserInfo = await response.json();

    // Update profile section
    const avatarEl = document.getElementById("profileAvatar");
    const nameEl = document.getElementById("profileName");
    const emailEl = document.getElementById("profileEmail");
    const roleEl = document.getElementById("profileRole");

    if (avatarEl) avatarEl.textContent = getEmailInitial(user.email);
    if (nameEl) nameEl.textContent = getNameFromEmail(user.email);
    if (emailEl) emailEl.textContent = user.email;
    if (roleEl) roleEl.textContent = user.role;

    cachedManageUrl = user.tenant.manage_subscription_url || null;
    displaySubscription(user.subscription, cachedManageUrl);

    if (!user.has_password) {
      const setPasswordSection = document.getElementById("setPasswordSection");
      if (setPasswordSection) {
        setPasswordSection.classList.remove("hidden");
      }
    }
  } catch (error) {
    console.error("Failed to load user info:", error);
    window.location.href = "/login";
  }
}

function displaySubscription(
  subscription: UserInfo["subscription"],
  manageUrl: string | null
): void {
  const subscriptionSection = document.getElementById("subscriptionSection");
  const tierEl = document.getElementById("subTier");
  const statusEl = document.getElementById("subStatus");
  const endsAtEl = document.getElementById("subEndsAt");
  const manageBtn = document.getElementById("manageSubscriptionBtn");

  if (!subscription && !manageUrl) {
    subscriptionSection?.classList.add("hidden");
    return;
  }

  subscriptionSection?.classList.remove("hidden");

  if (subscription) {
    if (tierEl) tierEl.textContent = subscription.tier || "Free";
    if (statusEl) statusEl.textContent = `${formatStatus(subscription.status)} subscription`;
    if (endsAtEl && subscription.subscription_ends_at) {
      endsAtEl.textContent = `${getEndDateLabel(subscription.status)} ${formatDate(subscription.subscription_ends_at)}`;
    }
  }

  if (manageUrl && manageBtn) {
    manageBtn.classList.remove("hidden");
  }
}

async function loadOAuthConnections(): Promise<void> {
  try {
    const [connectionsResponse, providersResponse] = await Promise.all([
      fetch("/api/auth/oauth/connections"),
      fetch("/api/auth/oauth/providers"),
    ]);

    if (!connectionsResponse.ok || !providersResponse.ok) {
      return;
    }

    const connectionsData = (await connectionsResponse.json()) as {
      connections: OAuthConnection[];
    };
    const providersData = (await providersResponse.json()) as {
      providers?: OAuthProvider[];
    };

    if (!providersData.providers || providersData.providers.length === 0) {
      return;
    }

    renderOAuthConnections(
      connectionsData.connections,
      providersData.providers
    );
  } catch (error) {
    console.error("Failed to load OAuth connections:", error);
  }
}

function renderOAuthConnections(
  connections: OAuthConnection[],
  allProviders: OAuthProvider[]
): void {
  const container = document.getElementById("oauthConnectionsList");
  if (!container) return;

  container.innerHTML = "";

  const connectedProviders = new Set(connections.map((c) => c.provider));

  // Render connected providers
  connections.forEach((connection) => {
    const card = document.createElement("div");
    card.className = "auth-method-card";
    card.innerHTML = `
      <div class="auth-method-left">
        <div class="auth-method-icon ${connection.provider}">
          <svg viewBox="0 0 24 24" fill="currentColor">
            ${PROVIDER_ICONS[connection.provider] || ""}
          </svg>
        </div>
        <div class="auth-method-details">
          <h4>${connection.provider.charAt(0).toUpperCase() + connection.provider.slice(1)}</h4>
          <p>${connection.provider_email || "Connected"} · ${formatDate(connection.connected_at)}</p>
        </div>
      </div>
      <button class="btn btn-ghost disconnect-btn" data-connection-id="${connection.id}">Disconnect</button>
    `;
    container.appendChild(card);
  });

  // Render unconnected providers
  allProviders.forEach((provider) => {
    if (!connectedProviders.has(provider.name)) {
      const card = document.createElement("div");
      card.className = "auth-method-card";
      card.innerHTML = `
        <div class="auth-method-left">
          <div class="auth-method-icon ${provider.name}">
            <svg viewBox="0 0 24 24" fill="currentColor">
              ${PROVIDER_ICONS[provider.name] || ""}
            </svg>
          </div>
          <div class="auth-method-details">
            <h4>${provider.displayName}</h4>
            <p>Not connected</p>
          </div>
        </div>
        <button class="btn btn-secondary connect-btn" data-provider="${provider.name}">Connect</button>
      `;
      container.appendChild(card);
    }
  });

  // Add event listeners
  container.querySelectorAll<HTMLButtonElement>(".disconnect-btn").forEach((btn) => {
    btn.addEventListener("click", () =>
      disconnectProvider(btn.dataset.connectionId || "")
    );
  });

  container.querySelectorAll<HTMLButtonElement>(".connect-btn").forEach((btn) => {
    btn.addEventListener("click", () =>
      connectProvider(btn.dataset.provider || "")
    );
  });
}

async function disconnectProvider(connectionId: string): Promise<void> {
  if (!confirm("Are you sure you want to disconnect this account?")) {
    return;
  }

  try {
    const response = await fetch(`/api/auth/oauth/connections/${connectionId}`, {
      method: "DELETE",
    });

    const result = (await response.json()) as { error?: string };

    if (response.ok) {
      showToast({ message: "Provider disconnected", type: "success" });
      loadOAuthConnections();
    } else {
      showToast({ message: result.error || "Failed to disconnect provider", type: "error" });
    }
  } catch (error) {
    console.error("Failed to disconnect provider:", error);
    showToast({ message: "Failed to disconnect provider", type: "error" });
  }
}

function connectProvider(provider: string): void {
  window.location.href = `/api/auth/oauth/${provider}/authorize?link=true`;
}

function logout(): void {
  window.location.href = "/api/auth/logout";
}

async function deleteAccount(): Promise<void> {
  const confirmed = confirm(
    "Are you sure you want to delete your account? This action cannot be undone."
  );

  if (!confirmed) return;

  try {
    const response = await fetch("/api/auth/me", { method: "DELETE" });

    if (response.ok) {
      showToast({ message: "Account deleted successfully", type: "success" });
      setTimeout(() => {
        window.location.href = "/login";
      }, 1500);
    } else {
      const errorData = (await response.json()) as { error?: string };
      showToast({
        message: "Failed to delete account: " + (errorData.error || "Unknown error"),
        type: "error",
      });
    }
  } catch (err) {
    console.error("Delete account failed:", err);
    showToast({ message: "Failed to delete account", type: "error" });
  }
}

function manageSubscription(): void {
  if (cachedManageUrl) {
    window.location.href = cachedManageUrl;
  } else {
    showToast({ message: "Subscription management URL not configured", type: "warning" });
  }
}

function goToSetPassword(): void {
  window.location.href = "/set-password";
}

function setupTabNavigation(): void {
  document.querySelectorAll<HTMLElement>(".nav-item[data-tab]").forEach((item) => {
    item.addEventListener("click", () => {
      const tabId = item.dataset.tab;
      if (!tabId) return;

      // Update nav items
      document.querySelectorAll(".nav-item[data-tab]").forEach((nav) => {
        nav.classList.remove("active");
      });
      item.classList.add("active");

      // Update panels
      document.querySelectorAll(".tab-panel").forEach((panel) => {
        panel.classList.remove("active");
      });
      document.getElementById(`${tabId}-panel`)?.classList.add("active");
    });
  });
}

function setupEventListeners(): void {
  document.getElementById("logoutBtn")?.addEventListener("click", logout);
  document.getElementById("deleteBtn")?.addEventListener("click", deleteAccount);
  document
    .getElementById("manageSubscriptionBtn")
    ?.addEventListener("click", manageSubscription);
  document
    .getElementById("setPasswordBtn")
    ?.addEventListener("click", goToSetPassword);
}

export function initAccount(): void {
  setupTabNavigation();
  loadUserInfo();
  loadOAuthConnections();
  setupEventListeners();
  initPasskeyManagement();
}
